import { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { knowledgeApi } from '../../lib/knowledge-api';
import KnowledgeFilters from './KnowledgeFilters';
import Dropdown from '../shared/Dropdown';
import { useTheme } from '../../hooks/useTheme';

export default function CreateItemModal({ isOpen, onClose, collections = [], onItemCreated, preselectedCollections = [] }) {
  const { currentThemeType, themeTypes } = useTheme();
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  const isLightTheme = currentThemeType === themeTypes.LIGHT;

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeFilters, setActiveFilters] = useState([]);

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsData = await knowledgeApi.filters.getProducts();
        setProducts(productsData);
        // Set first product as default
        if (productsData.length > 0) {
          setSelectedProductId(productsData[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    };

    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  // Set preselected collections when modal opens
  useEffect(() => {
    if (isOpen && preselectedCollections.length > 0) {
      setSelectedCollections(preselectedCollections);
    }
  }, [isOpen, preselectedCollections]);

  // Product options for dropdown
  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.id,
      label: product.name
    }));
  }, [products]);

  // Filter collections
  const filteredCollections = useMemo(() => {
    return collections.filter(collection => {
      // Handle null titles safely
      const title = collection.title || '';
      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesProduct = selectedProduct === 'all' || collection.product === selectedProduct;
      const matchesCategory = selectedCategory === 'all' || collection.category === selectedCategory;
      return matchesSearch && matchesProduct && matchesCategory;
    });
  }, [collections, searchTerm, selectedProduct, selectedCategory]);

  if (!isOpen) return null;

  const handleCollectionToggle = (collectionId) => {
    setSelectedCollections(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId);
      } else {
        return [...prev, collectionId];
      }
    });
  };

  const handleSave = async () => {
    // Validate content
    if (!content.trim()) {
      toast.error('Please enter some content for the item');
      return;
    }

    setIsLoading(true);

    try {
      // Build the request payload
      const payload = {
        content: content.trim(),
        collection_ids: selectedCollections,
      };

      // Include product_id if no collections selected and a product is chosen
      if (selectedCollections.length === 0 && selectedProductId) {
        payload.product_id = selectedProductId;
      }

      // Create the item via API
      const response = await knowledgeApi.items.create(payload);

      // Show success message
      toast.success(response.message || 'Item created successfully!');

      // Notify parent that item was created
      if (onItemCreated) {
        onItemCreated(response.item);
      }

      // Reset form and close modal
      setSelectedCollections([]);
      setContent('');
      onClose();
    } catch (error) {
      // Error handling is done by the API client interceptor
      console.error('Failed to create item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedCollections([]);
    setContent('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-5xl h-[700px] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Create Item</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content - Split View */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Side - Collections */}
          <div className="w-1/3 border-r border-gray-700 flex flex-col">
            <div className="p-4 border-b border-gray-700">
              <h4 className="text-sm font-semibold text-gray-400 mb-3">Link to Collections</h4>
              <KnowledgeFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                contextFilters={[]}
                onRemoveFilter={() => {}}
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredCollections.length > 0 ? (
                filteredCollections.map((collection) => (
                  <button
                    key={collection.id}
                    onClick={() => handleCollectionToggle(collection.id)}
                    className={`w-full p-4 text-left border-b transition-all duration-200 ${
                      selectedCollections.includes(collection.id)
                        ? isLightTheme
                          ? 'bg-rose-50 border-l-4 border-l-rose-400 border-b-rose-200'
                          : 'bg-purple-600/20 border-l-4 border-l-purple-500 border-b-gray-700'
                        : isLightTheme
                          ? 'border-b-gray-200 hover:bg-gray-100'
                          : 'border-b-gray-700 hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold text-sm truncate ${isLightTheme ? 'text-gray-800' : 'text-white'}`} title={collection.title}>{collection.title}</div>
                        <div className={`text-xs mt-1 truncate ${isLightTheme ? 'text-gray-500' : 'text-gray-400'}`} title={collection.category}>{collection.category}</div>
                      </div>
                      {selectedCollections.includes(collection.id) && (
                        <svg className={`w-5 h-5 ml-4 ${isLightTheme ? 'text-rose-500' : 'text-purple-500'}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-8 text-center">
                  <p className="text-gray-400">
                    {searchTerm || selectedProduct !== 'all' || selectedCategory !== 'all'
                      ? 'No matching collections'
                      : 'No collections available'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-4 flex-1 flex flex-col overflow-hidden">
              {/* Content Input with embedded buttons */}
              <div className="flex flex-col flex-1 min-h-0">
                <label className="text-gray-400 text-sm font-semibold mb-2">Content</label>
                <div className="relative flex-1 flex flex-col border border-gray-700 rounded-lg focus-within:border-purple-500 transition-colors bg-gray-800">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="flex-1 w-full px-4 py-3 pb-14 bg-transparent text-white text-sm focus:outline-none resize-none"
                    placeholder="Enter the content for this knowledge item..."
                  />
                  <div className="border-t border-gray-700 px-3 py-3 flex items-center justify-end gap-2">
                    {/* Product Dropdown - Only show when no collections selected */}
                    {selectedCollections.length === 0 && productOptions.length > 0 && (
                      <div className="w-48">
                        <Dropdown
                          options={productOptions}
                          value={selectedProductId}
                          onChange={setSelectedProductId}
                          placeholder="Select Product"
                        />
                      </div>
                    )}
                    <button
                      onClick={() => console.log('Add Image (placeholder)')}
                      className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-all duration-200 border border-gray-600"
                      title="Add Image"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => console.log('Add Attachment (placeholder)')}
                      className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-all duration-200 border border-gray-600"
                      title="Add Attachment"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isLoading}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      style={{ color: 'var(--color-text-on-accent)' }}
                    >
                      {isLoading && (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      <span>{isLoading ? 'Creating...' : 'Create Item'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
